import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  COMPTE_STATUT_OPTIONS,
  FORMULES,
  utilisateurRoleLabel,
  type Compte,
  type CompteInput,
  type CompteStatut,
  type CompteType,
} from "../data/types";

interface CompteFormProps {
  open: boolean;
  onClose: () => void;
  type: CompteType;
  compte?: Compte;
}

const emptyForm: CompteInput = {
  nom: "",
  email: "",
  telephone: "",
  ville: "",
  responsable: "",
  role: "",
  formuleId: "",
  statut: "en_attente",
  notes: "",
};

const formuleOptions = FORMULES.map((formule) => ({
  value: formule.id,
  label:
    formule.prixMensuel === 0
      ? `${formule.nom} — Gratuit`
      : `${formule.nom} — ${formule.prixMensuel.toLocaleString("fr-FR")} FCFA/mois`,
}));

/**
 * Modification d'un compte existant (Super Admin).
 *
 * Seuls des champs réellement persistés et sûrs sont modifiables :
 *   - nom, téléphone (colonnes `comptes`) ;
 *   - formule d'abonnement (`comptes.formule_id`) ;
 *   - statut (`comptes.statut` / `comptes.actif`).
 *
 * L'e-mail de connexion et le rôle ne sont PAS modifiables ici : l'e-mail est
 * géré par Supabase Auth (le modifier dans `comptes` seul désynchroniserait le
 * compte) et le rôle est protégé côté serveur (trigger `proteger_compte`). Ces
 * valeurs sont affichées en lecture seule pour rester informatives.
 *
 * La CRÉATION de comptes clients n'est pas possible depuis le Super Admin : les
 * clients créent eux-mêmes leur compte depuis la page publique d'inscription.
 */
export default function CompteForm({ open, onClose, type, compte }: CompteFormProps) {
  const { updateCompte } = useSuperAdmin();

  const [form, setForm] = useState<CompteInput>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (compte) {
      setForm({
        nom: compte.nom,
        email: compte.email,
        telephone: compte.telephone,
        ville: compte.ville,
        responsable: compte.responsable,
        role: compte.role,
        formuleId: compte.formuleId,
        statut: compte.statut,
        notes: compte.notes,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, compte]);

  const update = (key: keyof CompteInput, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = () => {
    if (!compte) {
      onClose();
      return;
    }
    const nextErrors: Record<string, string> = {};
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    updateCompte(compte.id, { ...form, nom: form.nom.trim() });
    onClose();
  };

  const nomLabel = type === "agence" ? "Nom de l'agence" : "Nom complet";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={compte ? `Modifier — ${compte.nom}` : "Modifier le compte"}
      description="Mettez à jour les informations autorisées du compte. Toute modification est tracée dans le journal d'audit."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={nomLabel} htmlFor="compte-nom" required error={errors.nom} className="sm:col-span-2">
            <Input
              id="compte-nom"
              value={form.nom}
              invalid={Boolean(errors.nom)}
              onChange={(event) => update("nom", event.target.value)}
              placeholder={type === "agence" ? "Agence Immobilière du Faso" : "Nom et prénom"}
            />
          </Field>

          <Field
            label="Email de connexion"
            htmlFor="compte-email"
            hint="Géré par l'authentification : non modifiable depuis cet écran."
          >
            <Input id="compte-email" type="email" icon="ri-mail-line" value={form.email} readOnly disabled />
          </Field>

          <Field label="Téléphone" htmlFor="compte-telephone">
            <Input
              id="compte-telephone"
              icon="ri-phone-line"
              value={form.telephone}
              onChange={(event) => update("telephone", event.target.value)}
              placeholder="+226 70 00 00 00"
            />
          </Field>

          {type === "utilisateur" && (
            <Field label="Rôle" htmlFor="compte-role" hint="Protégé côté serveur : non modifiable depuis cet écran.">
              <Input id="compte-role" value={utilisateurRoleLabel(compte?.role ?? "")} readOnly disabled />
            </Field>
          )}

          {type !== "utilisateur" && (
            <Field label="Formule d'abonnement" htmlFor="compte-formule">
              <Select
                id="compte-formule"
                value={form.formuleId}
                onChange={(event) => update("formuleId", event.target.value)}
                options={formuleOptions}
                placeholder="Aucune formule"
              />
            </Field>
          )}

          <Field
            label="Statut du compte"
            htmlFor="compte-statut"
            hint="Suspendre / désactiver fait aussi basculer l'accès (actif) en conséquence."
          >
            <Select
              id="compte-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value as CompteStatut)}
              options={COMPTE_STATUT_OPTIONS}
            />
          </Field>
        </div>

        <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs leading-relaxed text-foreground-600">
          <i className="ri-information-line mt-0.5 shrink-0 text-sm text-foreground-500" aria-hidden="true" />
          Les champs sensibles (rôle, statut d'authentification, formulaire) sont vérifiés côté serveur. Un
          administrateur ne peut pas s'attribuer un rôle ni un statut par une simple requête.
        </p>
      </div>
    </Modal>
  );
}